// approvalFailure.js — the ONE pure view-model for an approve-then-apply FAILURE on an approval
// card, shared by both surfaces (AgentStudioChat's ChatInteractionCard and the board's
// SessionDrawer) so the two can never drift. No React, no DOM, no imports — unit-testable under
// bare `node --test`.
//
// ─────────────────────────── THE BUG THIS EXISTS FOR (P2) ───────────────────────────
// A human approved a Meta-create card; the apply failed on Graph. By design the server rolls the
// whole transaction back — no nonce burned, the MoneyAction returns to awaiting_approval — and
// 403s with { error: 'capability_rejected', reason: 'apply_refused: …' }. The rollback is CORRECT:
// a retry must stay possible. What was wrong is that the CARD kept rendering a plain "Approve" as
// if the click had never happened, so the failure read as a HANG and a second Approve silently
// re-ran the whole funnel.
//
// The card therefore has to say three things: it FAILED, in plain language WHY, and that the
// button is now a RETRY of something that already ran once.
//
// TWO SOURCES, ONE SHAPE. The server hands the same `apply_failure` stamp
// (Agent::ApplyFailureStamp) through two doors:
//   * IMMEDIATE — in the 403 response body, so the surface that just POSTed flips without a refetch
//   * DURABLE   — on the card the surface reads on load (chat: the interaction payload, merged from
//                 MoneyAction#card at serialization time; board: Task.payload via BoardController)
// `applyFailureFrom` normalises either into the same object, so a component holds ONE piece of
// state fed by both.

// The stamp keys the server writes. Kept as a normalised shape rather than passing the raw jsonb
// around, so a server-side rename is a one-line change here and not a hunt through two components.
export function applyFailureFrom(raw) {
  if (!raw || typeof raw !== "object") return null
  const message = String(raw.message || "").trim()
  const reason = String(raw.reason || "").trim()
  // A stamp with neither piece of copy is not renderable — treat it as absent rather than showing
  // an empty red box.
  if (!message && !reason) return null
  const count = Number(raw.count) || 1
  return {
    // Plain language for the human. The raw machine reason is the fallback, never the first choice.
    message: message || reason,
    step: raw.step ? String(raw.step) : null,
    count: count < 1 ? 1 : count,
    at: raw.at ? String(raw.at) : null,
  }
}

// Pull the stamp out of an axios rejection (`err.response.data.apply_failure`). Returns null for
// every other error shape — a network blip, a confirm_required 403, a plain capability rejection —
// so only a REAL apply failure ever flips the card into the errored state.
export function applyFailureFromError(err) {
  const data = err && err.response && err.response.data
  return applyFailureFrom(data && data.apply_failure)
}

// The approve button's label. After a failure it is an explicit RETRY, because the human needs to
// know the click re-runs something that already reached Meta once — "Approve" would read as if the
// first click never happened. It keeps .pgs-approve styling either way (repo hard rule: a primary
// action is never a gray/native button).
export function approveLabel(failure, { submitting = false } = {}) {
  if (submitting) return "Working…"
  return failure ? "Retry" : "Approve"
}

// The attempt line, shown ONLY once there is more than one attempt. Saying "Attempt 1" on a first
// failure is noise; saying "Attempt 2" is the fact that stops a human from clicking a third time
// without reading the error.
export function attemptLabel(failure) {
  if (!failure || failure.count <= 1) return null
  return `Attempt ${failure.count}`
}

// The whole errored block, as data. A component maps this to markup; it makes no rendering
// decisions of its own beyond that.
export function failureView(failure, { submitting = false } = {}) {
  if (!failure) return null
  return {
    message: failure.message,
    attempt: attemptLabel(failure),
    approveLabel: approveLabel(failure, { submitting }),
  }
}
